import { camelCaseKeys } from 'launchdarkly-react-client-sdk';
import { LOCAL_STORAGE_KEYS } from "../constants/Constants";
import PublicVariables from "../constants/PublicVariables";
import storage from "../services/storage";

// LaunchDarkly client instance and state tracking
let ldClientInstance = null;
let isLDInitialized = false;

/**
 * Convert all flag keys from kebab-case to camelCase using LaunchDarkly's official utility
 * This function uses camelCaseKeys from launchdarkly-react-client-sdk
 * @param {Object} flags - Object with kebab-case keys
 * @returns {Object} Object with camelCase keys
 */
export const convertFlagsToCamelCase = (flags) => {
    return camelCaseKeys(flags);
};

/**
 * Per-portal identifier. This is the ONLY value that changes between repos.
 * One of: "admin" | "showme" | "agent" | "journey"
 */
const PORTAL = "agent";

/**
 * Normalizes REACT_APP_APP_ENV (development | release-candidate | production,
 * and any close variants) into a short, stable env code for the app id.
 *
 * @param {string} raw - Raw REACT_APP_APP_ENV value
 * @returns {"dev" | "rc" | "prod"}
 */
const normalizeEnv = (raw) => {
    const v = (raw || "").toLowerCase();
    if (v.startsWith("prod")) return "prod";
    if (v === "rc" || v.startsWith("rel") || v.includes("candidate") || v.includes("staging")) return "rc";
    return "dev";
};

/**
 * Builds the LaunchDarkly client-side application id so web MAU is attributed
 * to this portal + environment instead of appearing as "Unknown".
 *
 * Format: co.blitzz.<portal>.web.<env>  e.g. co.blitzz.journey.web.prod
 *
 * @returns {string} Application id for LDOptions.application.id
 */
export const getLDApplicationId = () => `co.blitzz.${PORTAL}.web.${normalizeEnv(process.env.REACT_APP_APP_ENV)}`;

/**
 * Create LaunchDarkly context object from domain data
 * @param {Object} domainData - Domain information
 * @returns {Object} LaunchDarkly context object
 */
const createLDContext = (domainData) => {
    return {
        kind: 'user',
        key: domainData.domainId.toString(),
        name: domainData.domainName,
        email: domainData.domainEmail,
        custom: {
            ReleaseVersion: domainData.releaseVersion,
            Edition: domainData.edition,
            SubscriptionType: domainData.subscriptiontype,
            DomainStatus: "ACTIVE",
            IsTrial: Boolean(Number(domainData.IsTrial)),
            SubscriptionStatus: domainData.subscriptionstatus,
            PartnerName: domainData.partnername,
        }
    };
};

/**
 * Initialize LaunchDarkly SDK with user context
 * CRITICAL: Only call this AFTER you have a domainId
 * This prevents creating anonymous users that inflate MAU count
 *
 * If SDK is already initialized with a different user, it will use identify() to switch context
 * This prevents duplicate SDK instances and properly handles user switching
 *
 * @param {Object} domainData - Domain information containing domainId
 * @returns {Promise<Object|null>} LaunchDarkly client instance or null
 */
export const initializeLaunchDarkly = async (domainData) => {
    // Validate domainId exists to prevent anonymous user creation
    if (!domainData?.domainId) {
        console.error('[LaunchDarkly] Initialization blocked: No domainId provided');
        return null;
    }

    const context = createLDContext(domainData);

    try {
        // If already initialized, check if we need to identify a different user
        if (isLDInitialized && ldClientInstance) {
            const currentContext = ldClientInstance.getContext();

            // If same user (same domainId), return existing client
            if (currentContext && currentContext.key === context.key) {
                return ldClientInstance;
            }

            // Different user - use identify() to switch context without recreating client
            await ldClientInstance.identify(context);

            // Update flags after identifying new user
            const flags = ldClientInstance.allFlags();
            const camelCaseFlags = convertFlagsToCamelCase(flags);
            PublicVariables.ldFlags = camelCaseFlags;
            storage.set(LOCAL_STORAGE_KEYS.LDFLags, camelCaseFlags);

            return ldClientInstance;
        }

        // First time initialization - create new SDK instance
        const { initialize } = await import('launchdarkly-js-client-sdk');

        ldClientInstance = initialize(
            process.env.REACT_APP_LAUNCHDARKLY_CLIENTID,
            context,
            {
                bootstrap: 'localStorage',
                application: {
                    id: getLDApplicationId()
                }
            }
        );

        await ldClientInstance.waitForInitialization();
        isLDInitialized = true;

        // Convert flags from kebab-case to camelCase using LaunchDarkly's official utility
        const flags = ldClientInstance.allFlags();
        const camelCaseFlags = convertFlagsToCamelCase(flags);

        PublicVariables.ldFlags = camelCaseFlags;
        storage.set(LOCAL_STORAGE_KEYS.LDFLags, camelCaseFlags);

        return ldClientInstance;
    } catch (error) {
        console.error('[LaunchDarkly] Initialization failed:', error);
        isLDInitialized = false;
        ldClientInstance = null;
        return null;
    }
};

/**
 * Get the current LaunchDarkly client instance
 * @returns {Object|null} LaunchDarkly client or null
 */
export const getLDClient = () => ldClientInstance;

/**
 * Check if LaunchDarkly is initialized and ready
 * @returns {boolean} True if SDK is ready
 */
export const isLDReady = () => isLDInitialized && ldClientInstance !== null;

/**
 * Initialize LaunchDarkly from domain data (centralized helper)
 * Handles both custom domain and universal flow scenarios
 * @returns {Promise<Object|null>} LaunchDarkly client instance or null
 */
export const initializeLDFromStoredData = async () => {
    // Already initialized? Skip
    if (isLDReady()) {
        return ldClientInstance;
    }

    // Try to get domain data from App.js flow (custom domain)
    if (PublicVariables.domainDataForLD?.domainId) {
        return await initializeLaunchDarkly(PublicVariables.domainDataForLD);
    }

    // Try to get domain data from Header.js flow (universal flow after login)
    if (storage.has(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION) && storage.has(LOCAL_STORAGE_KEYS.USER_DATA)) {
        const userData = storage.get(LOCAL_STORAGE_KEYS.USER_DATA);
        const domainConfigData = storage.get(LOCAL_STORAGE_KEYS.DOMAIN_CONFIGURATION);

        if (userData?.domainId) {
            const domainData = {
                domainId: userData.domainId,
                domainName: userData.domainName,
                domainEmail: domainConfigData.configurations?.domainemail,
                releaseVersion: domainConfigData.configurations?.domainreleaseversion,
                edition: domainConfigData.configurations?.edition,
                subscriptiontype: domainConfigData.billingData?.subscriptiontype,
                IsTrial: PublicVariables.istrial,
                subscriptionstatus: domainConfigData.billingData?.subscriptionstatus,
                partnername: domainConfigData.billingData?.partnername
            };

            return await initializeLaunchDarkly(domainData);
        }
    }

    return null;
};

/**
 * Reset and close LaunchDarkly client (call on logout)
 * Ensures clean state for next login
 *
 * Note: This closes the SDK connection completely. On next login,
 * initializeLaunchDarkly will create a fresh connection with the new user context.
 */
export const resetLDIdentification = async () => {
    if (ldClientInstance) {
        try {
            // Flush any pending events before closing
            await ldClientInstance.flush();
            // Close the connection
            await ldClientInstance.close();
        } catch (error) {
            console.error('[LaunchDarkly] Error closing client:', error);
        }
    }

    ldClientInstance = null;
    isLDInitialized = false;
};
